import { z } from 'zod'
import i18next from 'i18next'
import { errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type {
  Coassign_Target_Cost_Other_Insert_Input,
  Coassign_Target_Cost_Other_Set_Input
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { type RawBuilder, sql } from 'kysely'
import { formatISO } from 'date-fns'
import { pick, filterPick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExist } from '@/lib/relation-graph'

function json<T>(value: T): RawBuilder<T> {
  return sql`${JSON.stringify(value)}`
}

const TABLE_NAME = 'target_cost_other' as const

export const targetCostOtherTableSchema = z.object({
  id: z.string().max(21),
  name: z
    .string()
    .trim()
    .min(2, { message: i18next.t('stages.labels.error.min2Chars') })
    .max(128, { message: i18next.t('stages.labels.error.max128Chars') }),
  project_id: z.string().max(21).refine(isExist('project'), errorParentIsRemoved),
  costs_plan: z
    .string()
    .default('[]')
    .transform((value) => JSON.parse(value))
    .pipe(
      z.array(
        z.object({
          offset: z.number().min(0),
          cost: z.number().min(0).max(1000000000)
        })
      )
    )
})

const targetCostOtherTableColumns = Object.keys(targetCostOtherTableSchema.shape) as Array<
  keyof typeof targetCostOtherTableSchema.shape
>

export const targetCostOtherSchema = targetCostOtherTableSchema

export type TargetCostOther = z.input<typeof targetCostOtherSchema>

export class TargetCostOtherMutator extends Mutator<typeof targetCostOtherSchema> {
  static readonly stageableClassName = 'TargetCostOtherMutator'
  schema = targetCostOtherSchema
  table = TABLE_NAME

  static async load(id: string, projectId: string): Promise<Reactive<TargetCostOtherMutator>> {
    const self = this.createInstance()

    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    self.context = { id, project_id: projectId }
    if (base !== undefined) {
      const serializedBase = {
        ...base,
        costs_plan: JSON.stringify(base.costs_plan)
      }
      self.base = serializedBase
    }

    return self
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const queries: InsertQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = {
      ...filterPick(rawData, ...targetCostOtherTableColumns),
      ...{
        costs_plan: json(rawData.costs_plan)
      },
      ...{ tenant_id },
      created_at: new Date().toISOString()
    }
    queries.push(DataBase.insertInto(this.table).values(sqliteParams).compile())
    return queries
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const target_cost_other_id = this.context.id!
    const queries: UpdateQuery[] = []
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const sqliteParams = {
      ...filterPick(modifiedParams, ...targetCostOtherTableColumns),
      ...{
        costs_plan: json(rawData.costs_plan)
      }
    }
    queries.push(
      DataBase.updateTable(this.table)
        .set(sqliteParams)
        .where('id', '=', target_cost_other_id)
        .compile()
    )

    return queries
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const target_cost_other_id = this.context.id!

    return [DataBase.deleteFrom(this.table).where('id', '=', target_cost_other_id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const mutations: MutationOperationAndVariables[] = []
    const rawData = { ...(await this.parsed)! }
    const projectParams = { ...filterPick(rawData, ...targetCostOtherTableColumns) }
    const variables: Coassign_Target_Cost_Other_Insert_Input = {
      ...projectParams,
      ...{ commit_id }
    }
    mutations.push({
      mutation: insertTargetCostOtherMutationDocument,
      variables: { input: variables }
    })

    return mutations
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const mutations: MutationOperationAndVariables[] = []
    const targetCostOtherId = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const projectParams = { ...filterPick(modifiedParams, ...targetCostOtherTableColumns) }
    const variables: Coassign_Target_Cost_Other_Set_Input = { ...projectParams, ...{ commit_id } }
    mutations.push({
      mutation: updateTargetCostOtherMutationDocument,
      variables: {
        id: targetCostOtherId,
        input: variables
      }
    })

    return mutations
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const deleted_at = formatISO(new Date())
    return [
      {
        mutation: updateDeletedAtTargetCostOtherMutationDocument,
        variables: { target_cost_other_id: id, deletedAt: deleted_at, commit_id: commitId }
      }
    ]
  }
}

const insertTargetCostOtherMutationDocument = graphql(/* GraphQL */ `
  mutation insertTargetCostOther($input: coassign_target_cost_other_insert_input!) {
    insert_coassign_target_cost_other_one(object: $input) {
      created_at
    }
  }
`)

const updateTargetCostOtherMutationDocument = graphql(/* GraphQL */ `
  mutation updateTargetCostOther($id: String!, $input: coassign_target_cost_other_set_input!) {
    update_coassign_target_cost_other_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)

const updateDeletedAtTargetCostOtherMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtTargetCostOtherMutationDocument(
    $target_cost_other_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_target_cost_other(
      where: { id: { _eq: $target_cost_other_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)
